import { Link, Outlet, useMatches } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { can, type PermissionCheck } from '@/lib/can';
import { t } from '@/lib/i18n';
import { useMe } from '@/lib/me';
import { Button, EmptyState } from '@/ui';
import s from './App.module.css';

declare module '@tanstack/router-core' {
  interface StaticDataRouteOption {
    /**
     * What a page needs, as its nav item declares it: `staticData: { permission: 'core.ai.admin' }`.
     * Someone who opens the address without it gets one "no access" page instead of a page whose
     * every card fails with 403. The API still enforces every call; this only saves the detour.
     */
    permission?: PermissionCheck;
  }
}

/** The shell's outlet, with the matched pages' permissions checked first. */
export function RouteAccess() {
  const me = useMe();
  const needed = useMatches({ select: (ms) => ms.map((m) => m.staticData.permission).filter((p) => p !== undefined) });
  if (needed.every((p) => can(me, p))) return <Outlet />;
  return (
    <div className={s.notFound}>
      <EmptyState
        icon={Lock}
        title={t('You do not have access to this page')}
        description={t('Your role does not include it. An administrator can give you access.')}
        action={
          <Link to="/">
            <Button variant="primary">{t('Go to Home')}</Button>
          </Link>
        }
      />
    </div>
  );
}
